import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  RatecardListUIResponse,
  RatecardUIResponse,
  StoreRateCardAssociationListUIResponse,
  StringArrayUIResponse,
} from "../../models/ApiResponse";
import agent from "../../api/agent";
import { RatecardResponse } from "../../../pages/clients/ratecard/dtos/RateCardResponse";
import { AddRateCardRequest } from "../../../pages/clients/ratecard/dtos/AddRateCardRequest";
import { StoreRateCardAssociationResponse } from "../../../pages/clients/ratecard/dtos/StoreRatecardAssociationResponse";
import { UpdateRateCardRequest } from "../../../pages/clients/ratecard/dtos/UpdateRateCardRequest";

type InitialState = {
  isLoading: boolean;
  ratecardList: RatecardResponse[];
  currentRatecard: RatecardResponse | undefined;
  storeRateCardAssociationList: StoreRateCardAssociationResponse[];
  regionNameList: string[];
  errorMessage: string | null;
};

const initialState: InitialState = {
  isLoading: false,
  ratecardList: [],
  currentRatecard: undefined,
  storeRateCardAssociationList: [],
  regionNameList: [],
  errorMessage: null,
};

export const getRatecardList = createAsyncThunk<RatecardListUIResponse, number>(
  "ratecard/rate-card-list-response",
  async (clientId: number) => {
    const response = await agent.RatecardAgent.getRateCardList(clientId);
    return response;
  }
);

export const getRatecardById = createAsyncThunk<RatecardUIResponse, number>(
  "ratecard/rate-card-response",
  async (id: number) => {
    const response = await agent.RatecardAgent.getRateCardById(id);
    return response;
  }
);

export const fetchStoreRateCardAssociationList = createAsyncThunk<
  StoreRateCardAssociationListUIResponse,
  number
>(
  "ratecard/fetch-store-ratecard-association-response",
  async (clientId: number) => {
    const response =
      await agent.RatecardAgent.fetchStoreRateCardAssociationList(clientId);
    return response;
  }
);

export const fetchRegionNameListForFuelSurcharge = createAsyncThunk<
  StringArrayUIResponse,
  { fuelType: string }
>(
  "ratecard/fetch-region-name-for-fuel-surcharge-response",
  async ({ fuelType }) => {
    const response =
      await agent.RatecardAgent.fetchRegionNameListForFuelSurcharge(fuelType);

    return response;
  }
);

export const addNewRatecard = createAsyncThunk<
  RatecardListUIResponse,
  { clientId: number; ratecardData: AddRateCardRequest }
>("ratecard/add-new-ratecard-response", async ({ clientId, ratecardData }) => {
  const response = await agent.RatecardAgent.addRateCard(
    clientId,
    ratecardData
  );
  return response;
});

export const updateRatecard = createAsyncThunk<
  RatecardUIResponse,
  { ratecardId: number; updateRatecardData: UpdateRateCardRequest }
>(
  "ratecard/update-ratecard-response",
  async ({ ratecardId, updateRatecardData }) => {
    const response = await agent.RatecardAgent.updateRatecard(
      ratecardId,
      updateRatecardData
    );
    return response;
  }
);

const ratecardSlice = createSlice({
  name: "ratecard",
  initialState,
  reducers: {
    clearCurrentRatecard: (state) => {
      state.currentRatecard = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // RATECARD LIST
      .addCase(getRatecardList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRatecardList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.ratecardList = action.payload.data;
        } else {
          state.ratecardList = [];
        }
      })
      .addCase(getRatecardList.rejected, (state, action) => {
        state.isLoading = false;
        state.ratecardList = [];
        state.errorMessage = action.error.message!;
      })
      // RATECARD BY ID
      .addCase(getRatecardById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getRatecardById.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.currentRatecard = action.payload.data;
        } else {
          state.ratecardList = [];
        }
      })
      .addCase(getRatecardById.rejected, (state, action) => {
        state.isLoading = false;
        state.currentRatecard = undefined;
        state.errorMessage = action.error.message!;
      })
      // ASSOCIATION LIST
      .addCase(fetchStoreRateCardAssociationList.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchStoreRateCardAssociationList.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload.isSuccess) {
          state.storeRateCardAssociationList = action.payload.data;
        } else {
          state.storeRateCardAssociationList = [];
        }
      })
      .addCase(fetchStoreRateCardAssociationList.rejected, (state, action) => {
        state.isLoading = false;
        state.storeRateCardAssociationList = [];
        state.errorMessage = action.error.message!;
      })
      // REGION NAME LIST
      .addCase(fetchRegionNameListForFuelSurcharge.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchRegionNameListForFuelSurcharge.fulfilled,
        (state, action) => {
          state.isLoading = false;
          if (action.payload.isSuccess) {
            state.regionNameList = action.payload.data;
          } else {
            state.regionNameList = [];
          }
        }
      )
      .addCase(
        fetchRegionNameListForFuelSurcharge.rejected,
        (state, action) => {
          state.isLoading = false;
          state.regionNameList = [];
          state.errorMessage = action.error.message!;
        }
      );
  },
});

export default ratecardSlice.reducer;
export const { clearCurrentRatecard } = ratecardSlice.actions;
